import { useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TeeButton } from '@/components/ui/tee-button';
import { supportAssistantService } from '@/services/support-assistant.service';
import type { AssistantMessageFeedback } from '@/types/support-assistant';

export interface AssistantFeedbackProps {
  messageId: string;
  value?: AssistantMessageFeedback;
}

const pressedClass = 'text-[var(--color-forest)] bg-[var(--background-badge-green)]';
const unpressedClass = 'text-[var(--text-content-sub-title)]';

export function AssistantFeedback({ messageId, value }: AssistantFeedbackProps) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<AssistantMessageFeedback | null>(value ?? null);
  const [busy, setBusy] = useState(false);

  const click = async (clicked: AssistantMessageFeedback) => {
    if (busy) return;
    const next = current === clicked ? null : clicked;
    const previous = current;
    setCurrent(next);
    setBusy(true);
    try {
      await supportAssistantService.setMessageFeedback(messageId, next);
    } catch {
      setCurrent(previous);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex items-center gap-1"
      role="group"
      aria-label={t('assistant.feedback.label')}
    >
      <TeeButton
        variant="ghost"
        size="s"
        leftIcon={<ThumbsUp size={14} aria-hidden="true" />}
        aria-label={t('assistant.feedback.helpful')}
        aria-pressed={current === 'up'}
        disabled={busy}
        className={current === 'up' ? pressedClass : unpressedClass}
        onClick={() => click('up')}
      />
      <TeeButton
        variant="ghost"
        size="s"
        leftIcon={<ThumbsDown size={14} aria-hidden="true" />}
        aria-label={t('assistant.feedback.notHelpful')}
        aria-pressed={current === 'down'}
        disabled={busy}
        className={current === 'down' ? pressedClass : unpressedClass}
        onClick={() => click('down')}
      />
    </div>
  );
}
